// =============================================================================
// Origin UI — src/pages/compliance/admin/ComplianceArticlesAdmin.tsx
// CM-14 — Compliance news & articles management (Compliance role only).
// Create / edit / activate / delete articles that surface on the all-staff
// Compliance landing page. Backend CRUD is from CM-4.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { complianceApi } from '@/services/complianceApi';
import type { ComplianceArticle } from '@/services/complianceApi';
import styles from '../compliance.module.css';

type Mode = 'list' | 'create' | 'edit';

export function ComplianceArticlesAdmin() {
  const { roles, rolesLoaded } = useAuth();
  const navigate = useNavigate();

  const [articles, setArticles] = useState<ComplianceArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadArticles() {
    try {
      setLoading(true);
      setArticles(await complianceApi.adminGetArticles());
    } catch (err) {
      console.warn('Failed to load articles', err);
      setError('Could not load articles.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (roles.isComplianceExec) loadArticles();
  }, [roles.isComplianceExec]);

  function startCreate() {
    setEditingId(null);
    setTitle('');
    setBody('');
    setIsActive(true);
    setMode('create');
    setError(null);
    setNotice(null);
  }

  function startEdit(a: ComplianceArticle) {
    setEditingId(a.complianceArticleId);
    setTitle(a.title);
    setBody(a.body);
    setIsActive(a.isActive);
    setMode('edit');
    setError(null);
    setNotice(null);
  }

  async function save() {
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!body.trim()) {
      setError('Body is required.');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await complianceApi.adminCreateArticle({ title: title.trim(), body: body.trim() });
        setNotice('Article published.');
      } else if (editingId) {
        await complianceApi.adminUpdateArticle(editingId, {
          title: title.trim(),
          body: body.trim(),
          isActive,
        });
        setNotice('Article updated.');
      }
      setMode('list');
      await loadArticles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(a: ComplianceArticle) {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await complianceApi.adminDeleteArticle(a.complianceArticleId);
      setNotice('Article deleted.');
      await loadArticles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  if (!rolesLoaded) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading&hellip;</div>
      </div>
    );
  }
  if (!roles.isComplianceExec) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Not authorised</h1>
          <p className={styles.subtitle}>News management is for Compliance team members only.</p>
        </header>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/compliance/dashboard')}>
          ← Dashboard
        </button>
        <h1 className={styles.title}>Compliance news &amp; articles</h1>
        <p className={styles.subtitle}>
          Published articles appear on the Compliance page for all staff.
        </p>
      </header>

      {error && <div className={styles.error}>{error}</div>}
      {notice && <div className={styles.notice}>{notice}</div>}

      {mode === 'list' && (
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <button className={styles.openBtn} onClick={startCreate}>
              + New article
            </button>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading&hellip;</div>
          ) : articles.length === 0 ? (
            <div className={styles.emptyState}>No articles yet.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Published</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.complianceArticleId}>
                    <td>
                      <div className={styles.cellName}>{a.title}</div>
                      <div className={styles.cellSub}>{a.publishedBy ?? ''}</div>
                    </td>
                    <td>
                      {new Date(a.publishedAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          a.isActive ? styles.statusDone : styles.statusOutstanding
                        }`}
                      >
                        {a.isActive ? 'Published' : 'Hidden'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.linkBtn} onClick={() => startEdit(a)}>
                          Edit
                        </button>
                        <button className={styles.linkBtnDanger} onClick={() => remove(a)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>{mode === 'create' ? 'New article' : 'Edit article'}</h2>
          <div className={styles.adminForm}>
            <div className={styles.adminField}>
              <label className={styles.adminLabel}>Title</label>
              <input
                className={styles.adminInput}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title"
              />
            </div>
            <div className={styles.adminField}>
              <label className={styles.adminLabel}>Body</label>
              <textarea
                className={styles.adminTextarea}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Article text"
              />
            </div>
            {mode === 'edit' && (
              <div className={styles.adminField}>
                <label className={styles.gatingRow}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>Published (visible to staff)</span>
                </label>
              </div>
            )}
            <div className={styles.formActions}>
              <button
                className={styles.secondaryBtn}
                onClick={() => {
                  setMode('list');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button className={styles.openBtn} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : mode === 'create' ? 'Publish' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
